/**
 * PEEKAPAYA-CUSTOM_client-side tab navigation for girls/boys routes
 * Client-Side Tab Navigation for Girls and Boys Pages
 * 
 * Intercepts clicks on "Girls" and "Boys" page navigation links, fetches the target 
 * page dynamically, and updates the storefront main content (#MainContent) 
 * without triggering a full page refresh.
 */

(function () {
  console.log('[Tab Navigation] Loaded and listening for page tab transitions...');

  /**
   * Helper to check if a pathname targets a tab page.
   * Matches if the URL is a page containing "girls" or "boys" (case-insensitive).
   * E.g. `/pages/girls-page`, `/en/pages/boys`, `/pages/girls`
   * @param {string} pathname 
   * @returns {boolean}
   */
  function isTabPage(pathname) {
    if (typeof pathname !== 'string') return false;
    const normalized = pathname.replace(/\/+$/, '') || '/';

    const homePath = (window.Shopify?.routes?.root || '/').replace(/\/+$/, '') || '/';
    if (normalized === homePath) return false;

    // NETSET-CUSTOM: Tab destinations come from Shopify menu resources whose
    // custom.audience metafield is girl/boy, never from hardcoded URL handles.
    return Array.from(document.querySelectorAll('a[data-audience="girl"], a[data-audience="boy"]')).some((link) => {
      try {
        const linkPath = new URL(link.href, window.location.origin).pathname.replace(/\/+$/, '') || '/';
        return linkPath === normalized;
      } catch (_) {
        return false;
      }
    });
  }
  /**
   * Loads a tab page dynamically using fetch, parses the HTML, 
   * and replaces the MainContent innerHTML.
   * @param {string} url 
   * @param {boolean} updateHistory 
   */
  async function loadTabPage(url, updateHistory = true) {
    console.log(`[Tab Navigation] Starting dynamic load for: ${url}`);
    const mainContent = document.getElementById('MainContent');
    if (!mainContent) {
      console.warn('[Tab Navigation] #MainContent element not found on page.');
      window.location.href = url;
      return;
    }

    // Apply fade out effect
    mainContent.style.transition = 'opacity 0.15s ease-in-out';
    mainContent.style.opacity = '0.3';

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const htmlText = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlText, 'text/html');

      const newMain = doc.getElementById('MainContent');
      if (newMain) {
        // Update document title
        if (doc.title) {
          document.title = doc.title;
        }

        // ------------------------------------------------------------------
        // PEEKAPAYA-CUSTOM: Sync <head> styles from the fetched page.
        // Shopify renders per-section <style> blocks into <head> via
        // {% style %} tags. When navigating from a different template
        // (e.g. collection â†’ girls/boys page), those section styles are
        // missing from the current <head>, causing broken layouts.
        // We compare by text content and only inject styles that are new.
        // ------------------------------------------------------------------
        const existingStyleTexts = new Set(
          Array.from(document.head.querySelectorAll('style')).map((s) => s.textContent.trim())
        );
        doc.head.querySelectorAll('style').forEach((fetchedStyle) => {
          const text = fetchedStyle.textContent.trim();
          if (text && !existingStyleTexts.has(text)) {
            const clone = document.createElement('style');
            clone.textContent = fetchedStyle.textContent;
            document.head.appendChild(clone);
          }
        });

        // Sync body's data-template attribute so template-scoped CSS selectors work
        const fetchedBody = doc.body;
        if (fetchedBody) {
          Array.from(fetchedBody.attributes).forEach((attr) => {
            // Only sync data-* and class attributes; avoid overwriting layout-critical ones
            if (attr.name.startsWith('data-') || attr.name === 'class') {
              document.body.setAttribute(attr.name, attr.value);
            }
          });
        }

        // Swap content
        mainContent.innerHTML = newMain.innerHTML;

        // Sync main content attributes
        Array.from(newMain.attributes).forEach((attr) => {
          mainContent.setAttribute(attr.name, attr.value);
        });

        // Re-execute script tags inside the new main content
        const scripts = mainContent.querySelectorAll('script');
        scripts.forEach((oldScript) => {
          try {
            const newScript = document.createElement('script');
            Array.from(oldScript.attributes).forEach((attr) => {
              newScript.setAttribute(attr.name, attr.value);
            });
            newScript.textContent = oldScript.textContent;
            oldScript.parentNode.replaceChild(newScript, oldScript);
          } catch (scriptErr) {
            console.warn('[Tab Navigation] Could not re-execute inline script:', scriptErr);
          }
        });
        // NETSET-CUSTOM: Sync the metafield-derived audience from the fetched page.
        const fetchedAudience = doc.documentElement.dataset.activeAudience || '';
        document.documentElement.dataset.activeAudience = fetchedAudience;
        updateActiveMenuLinks(url, fetchedAudience);

        // Update history state
        if (updateHistory) {
          history.pushState({ isTabPage: true, url }, '', url);
        }

        // Notify Shopify dev tools (webmcp / theme editor) that new content
        // has been injected so they can re-attach their listeners.
        try {
          document.dispatchEvent(new CustomEvent('shopify:section:load', {
            bubbles: true,
            detail: { sectionId: 'main-content' }
          }));
        } catch (_) {}

        // Scroll to the top of the page smoothly
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
        
        console.log('[Tab Navigation] Page content updated successfully!');
      } else {
        throw new Error('#MainContent element not found in the fetched page.');
      }
    } catch (error) {
      console.error('[Tab Navigation Error]:', error);
      // Fallback to traditional redirect on failure
      if (updateHistory) {
        window.location.href = url;
      }
    } finally {
      // Fade back in
      mainContent.style.opacity = '1';
    }
  }

  /**
   * Updates the CSS classes and aria attributes of active links in the header menus.
   * @param {string} urlStr 
   */
  function normalizeAudience(value) {
    const audience = String(value || '').trim().toLowerCase();
    if (audience === 'girls') return 'girl';
    if (audience === 'boys') return 'boy';
    if (audience === 'unisex') return 'both';
    return ['girl', 'boy', 'both'].includes(audience) ? audience : '';
  }

  function updateActiveMenuLinks(_urlStr, audienceOverride) {
    const activeAudience = normalizeAudience(
      audienceOverride ?? document.documentElement.dataset.activeAudience
    );

    document.querySelectorAll('header-component a[data-audience], header-menu a[data-audience], .menu-list a[data-audience]')
      .forEach((link) => {
        const linkAudience = normalizeAudience(link.dataset.audience);
        const shouldBeActive = activeAudience === 'both'
          ? ['girl', 'boy', 'both'].includes(linkAudience)
          : activeAudience !== '' && linkAudience === activeAudience;
        const parentListItem = link.closest('.menu-list__list-item');

        link.classList.toggle('menu-list__link--active', shouldBeActive);
        link.classList.toggle('menu-list__item--active', shouldBeActive);
        parentListItem?.classList.toggle('menu-list__list-item--active', shouldBeActive);
      });

    updateTabIndicator();
  }
  // Intercept click events â€” only for Girls/Boys tab pages
  document.addEventListener('click', function (e) {
    const link = e.target.closest('a');
    if (!link || e.defaultPrevented) return;

    // Exclude links with target="_blank" or download attribute
    if (link.hasAttribute('download') || link.getAttribute('target') === '_blank') {
      return;
    }

    // Verify it is a standard click on the same origin
    if (
      e.button === 0 &&
      !e.metaKey &&
      !e.ctrlKey &&
      !e.shiftKey &&
      !e.altKey &&
      link.origin === window.location.origin
    ) {
      // Don't intercept anchor links pointing to the same page
      if (link.pathname === window.location.pathname && link.hash) {
        return;
      }

      // Only intercept Girls/Boys tab pages â€” let everything else navigate normally
      // PEEKAPAYA-CUSTOM: Also require that we are ALREADY on a tab page.
      // If coming from a collection, product, or any other non-tab page,
      // a full browser navigation is needed to ensure all section styles,
      // JS modules and Shopify custom elements initialize correctly.
      if (isTabPage(link.pathname) && isTabPage(window.location.pathname)) {
        e.preventDefault();
        loadTabPage(link.href, true);
      }
    }
  });

  // Handle browser back and forward navigation (only for Girls/Boys tab pages)
  window.addEventListener('popstate', function (e) {
    const pathname = window.location.pathname;

    if (isTabPage(pathname)) {
      // Back/forward between Girls and Boys tabs â€” do a soft swap
      loadTabPage(window.location.href, false);
    }
    // For all other pages, the browser handles back/forward natively â€” no action needed
  });

  /**
   * Updates the width and horizontal position of the active sliding tab indicator.
   */
  function updateTabIndicator() {
    const activeTab = document.querySelector('.menu-list--mobile [data-audience].menu-list__link--active, .menu-list--mobile [data-audience].menu-list__item--active');
    const indicator = document.querySelector('.mobile-navigation-indicator');
    if (activeTab && indicator) {
      indicator.style.width = `${activeTab.offsetWidth}px`;
      indicator.style.transform = `translateX(${activeTab.offsetLeft}px)`;
    }
  }

  let activeStateFrame = 0;

  function scheduleActiveStateUpdate() {
    cancelAnimationFrame(activeStateFrame);
    activeStateFrame = requestAnimationFrame(() => {
      updateActiveMenuLinks(window.location.href);
    });
  }

  // Keep URL-derived state after Shopify replaces or hydrates header sections.
  const headerGroup = document.getElementById('header-group');
  if (headerGroup) {
    new MutationObserver(scheduleActiveStateUpdate).observe(headerGroup, {
      childList: true,
      subtree: true,
    });
  }

  document.addEventListener('shopify:section:load', scheduleActiveStateUpdate);
  window.addEventListener('pageshow', scheduleActiveStateUpdate);
  window.addEventListener('resize', updateTabIndicator);

  // Initialize from the URL, independently of whether the tab was clicked.
  updateActiveMenuLinks(window.location.href);
  window.addEventListener('load', scheduleActiveStateUpdate);
})();

